
/*
 * 既存のEngine依存データおよびEngine依存の仕組みから、
 * 改めてサブスクリプション申込完了画面用のデータ構造を精製するための手続き.
 *
 * 機能改修の場合、この手続をなんとかして改修しようとするのではなく、
 * 可能ならそもそもこの手続を解体して、全体最適化(≠処理速度)された構造を持ち込むのが望ましい.
 */

import { LegacyEngineSubscriptionHistory } from '@/models/api/subscription/legacy-engine-subscription-history'
import { CartItem } from '@/models/cart/cart-item'
import {
  FailedOrderResult,
  OrderResult,
  PaymentAmount,
  SuccessOrderResult
} from '@/models/order/completed/order-result'
import { Contact, Delivery, Order, OrderKind } from '@/models/order/order'
import { Order as ApiOrder, OrderPurchaseFail } from '@spa/services/order'
import { Contact as StoreContact } from '@spa/store/modules/contact/types'

interface BuildParameter {
  succeedHistories: LegacyEngineSubscriptionHistory[]
  failed: OrderPurchaseFail[]
  wholeOrderList: ApiOrder[]
}
export const buildAppliedSubscriptionRecordFromLegacyData =
  (params: BuildParameter): OrderResult[] => {
    /**
     * 一旦「一度に一つのサブスクリプション申込しかしない」ということを前提に、
     * すなわち wholeOrderList には常に1つの要素しか存在しないということを前提に、
     * 配列の先頭要素のみを取り出している.
     *
     * @see client/spa/src/components/pages/subscription/README.md
     * @see https://my-color.esa.io/posts/555
     */
    const orderParameter = params.wholeOrderList[0]

    const succeed: SuccessOrderResult[] = params.succeedHistories.map(
      history => new SuccessOrderResultConvertedFromLegacyRecords(
        history,
        /**
         * 一旦「一度に一つのサブスクリプション申込しかしない」ということを前提に、
         * すなわち wholeOrderList には常に1つの要素しか存在しないということを前提に、
         * 配列の先頭要素のみを取り出している.
         *
         * @see client/spa/src/components/pages/subscription/README.md
         * @see https://my-color.esa.io/posts/555
         */
        orderParameter
      )
    )
    const failed: FailedOrderResult[] = params.failed.map(
      result => new FailedOrderResultConvertedFromLegacyRecords(
        result, orderParameter
      )
    )

    return [...succeed, ...failed]
  }

export class DeliveryContactForOrderHistoryFromStoreDeliveryContact implements Delivery {
  constructor(private readonly storeDeliveryContact: Required<StoreContact>) {}

  get contact(): Contact {
    return {
      name: {
        first_name: this.storeDeliveryContact.first_name,
        last_name: this.storeDeliveryContact.last_name,
      },
      address: {
        country: this.storeDeliveryContact.country,
        code: this.storeDeliveryContact.postal_code,
        region: this.storeDeliveryContact.region,
        locality: this.storeDeliveryContact.locality,
        street: this.storeDeliveryContact.street_address,
      },
      tel: this.storeDeliveryContact.tel,
    }
  }
}

class SuccessOrderResultConvertedFromLegacyRecords implements SuccessOrderResult {
  readonly success = true
  readonly error = null

  constructor(
    private readonly history: LegacyEngineSubscriptionHistory,
    private readonly orderParameterForTheHistory: {
      kind: OrderKind,
      cartItems: CartItem[],
    }
  ) { }

  get order(): Order {
    return {
      id: this.history.id,
      shortId: this.history.shortId,

      kind: this.orderParameterForTheHistory.kind,
      cartItems: this.orderParameterForTheHistory.cartItems,
    }
  }

  get paymentAmount(): PaymentAmount {
    return {
      total: this.history.totalPrice,
      tax: this.history.tax,
    }
  }
}
class FailedOrderResultConvertedFromLegacyRecords implements FailedOrderResult {
  readonly success = false

  constructor(
    private readonly result: OrderPurchaseFail,
    private readonly orderParameter: ApiOrder
  ) {}

  get error(): Error {
    return this.result.error
  }

  get order(): Order {
    return {

      ...this.orderParameter,
      id: 'failed-order',
      shortId: 'failed-order',
    }
  }
}
